import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDevice } from '../context/DeviceContext';
import { AddIcon, DeleteIcon } from './icons';
import { LoadingDisplay } from './utils';
import { BridgeInterfaces } from './BridgeInterfaces';

export const ModifyAutoConfigTab = () => {
    const { device, api } = useDevice();
    const [isLoading, setIsLoading] = useState(true);
    const [interfaces, setInterfaces] = useState([]);
    const [updateSelections, setUpdateSelections] = useState({
        updateBandwidth: true,
        updateInterfaces: true,
    });

    const { register, handleSubmit, control, watch, getValues, reset, formState: { errors, isDirty, isSubmitting }, trigger, setValue } = useForm({
        defaultValues: {
            enableHotspot: true,
            enablePppoe: true,
            userDownload: 5,
            userUpload: 5,
            vendoInstances: [{ interfaces: [{ value: '' }], targetSubnet: '10.10.10.0/23' }],
            pppoeInterfaceAssignment: ''
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "vendoInstances"
    });

    // Automatically set the targetSubnet when bridges are added or removed
    useEffect(() => {
        fields.forEach((item, index) => {
            const newSubnet = `10.10.${10 + index * 10}.0/23`;
            // Check if the current value is already correct to avoid unnecessary re-renders/dirtying the form
            if (getValues(`vendoInstances.${index}.targetSubnet`) !== newSubnet) {
                 setValue(`vendoInstances.${index}.targetSubnet`, newSubnet, { shouldDirty: true });
            }
        });
    }, [fields, setValue, getValues]);


    const enableHotspot = watch('enableHotspot');
    const enablePppoe = watch('enablePppoe');

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            api.getProvisionSettings(device.id),
            api.getInterfaces(device.id)
        ]).then(([settingsData, interfacesData]) => {
            reset(settingsData);
            setInterfaces(interfacesData || []);
        }).catch(err => {
            toast.error("Could not load current device settings.");
        }).finally(() => {
            setIsLoading(false);
        });
    }, [device.id, api, reset]);
    
    const onUpdate = async (data) => {
        if (!updateSelections.updateBandwidth && !updateSelections.updateInterfaces) {
            return toast.error("Please select at least one section to update.");
        }

        const payload = {
            selections: updateSelections,
            settings: data,
        };

        await toast.promise(
            api.updateProvisionSettings(device.id, payload),
            {
                loading: 'Applying updates to device...',
                success: (res) => {
                    reset(data);
                    return "Configuration updated successfully!";
                },
                error: (err) => err.response?.data?.message || 'Failed to update configuration.',
            }
        );
    };

    const validateInterfaceUniqueness = (currentValue, currentFieldName) => {
        if (!currentValue) return true;
    
        const allOtherAssignments = new Set();
        const formData = getValues();
        
        if (formData.enablePppoe && formData.pppoeInterfaceAssignment && currentFieldName !== 'pppoeInterfaceAssignment') {
            allOtherAssignments.add(formData.pppoeInterfaceAssignment);
        }
        
        if (formData.enableHotspot && formData.vendoInstances) {
            formData.vendoInstances.forEach((instance, bridgeIndex) => {
                instance.interfaces.forEach((iface, ifaceIndex) => {
                    const fieldId = `${bridgeIndex}-${ifaceIndex}`;
                    if (iface.value && currentFieldName !== fieldId) {
                        allOtherAssignments.add(iface.value);
                    }
                });
            });
        }
        
        return !allOtherAssignments.has(currentValue) || 'This interface is already assigned elsewhere.';
    };
    
    if(isLoading) return <LoadingDisplay message="Reading current configuration from device..." />;

    return (
        <div>
            <h2 className="text-xl font-semibold mb-4">Modify Auto-Configuration</h2>
            <p className="text-sm text-gray-600 mb-4">Select the sections you wish to update, make your changes, and then apply them. The script will only modify the selected parts of the configuration.</p>
            <form onSubmit={handleSubmit(onUpdate)} className="space-y-6 bg-gray-50 p-6 rounded-lg">
                 <fieldset className="p-4 border rounded-lg">
                    <legend className="font-semibold text-gray-700 px-2 flex items-center">
                        <input 
                            type="checkbox" 
                            className="h-4 w-4 rounded mr-2"
                            checked={updateSelections.updateBandwidth}
                            onChange={e => setUpdateSelections(s => ({ ...s, updateBandwidth: e.target.checked }))}
                        />
                        Hotspot QoS (Simple Queues)
                    </legend>
                    <div className={!updateSelections.updateBandwidth ? 'opacity-50 pointer-events-none' : ''}>
                        <p className="text-sm text-gray-600 my-2 px-1">Configure a **Simple Queue** for each Hotspot bridge. This applies a shared speed limit to all users connected to that bridge, using FQ-CoDel for fair traffic distribution.</p>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                            <div><label className="text-sm">Bridge Max Download (Mbps)</label><input type="number" {...register('userDownload', { required: true, valueAsNumber: true })} className="input-class w-full" /></div>
                            <div><label className="text-sm">Bridge Max Upload (Mbps)</label><input type="number" {...register('userUpload', { required: true, valueAsNumber: true })} className="input-class w-full" /></div>
                        </div>
                    </div>
                </fieldset>

                <fieldset className="p-4 border rounded-lg">
                    <legend className="font-semibold text-gray-700 px-2 flex items-center">
                         <input 
                            type="checkbox" 
                            className="h-4 w-4 rounded mr-2"
                            checked={updateSelections.updateInterfaces}
                            onChange={e => setUpdateSelections(s => ({ ...s, updateInterfaces: e.target.checked }))}
                        />
                        Interface & Bridge Assignments
                    </legend>
                    <div className={`space-y-4 mt-2 ${!updateSelections.updateInterfaces ? 'opacity-50 pointer-events-none' : ''}`}>
                        {enableHotspot && (
                            <div>
                                <h3 className="font-medium text-gray-800 px-2">Vendo Hotspot Bridges</h3>
                                {fields.map((item, bridgeIndex) => (
                                     <div key={item.id} className="p-3 border rounded-md mt-2 bg-white">
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="font-medium text-gray-600">
                                                {bridgeIndex === 0 ? `main-vendo-bridge` : `sub${bridgeIndex}-vendo-bridge`}
                                            </label>
                                            <button type="button" onClick={() => remove(bridgeIndex)} className="p-2 text-red-500 hover:text-red-700"><DeleteIcon /></button>
                                        </div>
                                        <div className="mb-3">
                                            <label className="text-sm font-medium text-gray-500">Target Subnet (for Simple Queue)</label>
                                            <input
                                                {...register(`vendoInstances.${bridgeIndex}.targetSubnet`)}
                                                className="input-class w-full mt-1 bg-gray-100 cursor-not-allowed"
                                                readOnly
                                                placeholder="Auto-generated subnet"
                                            />
                                        </div>
                                        <label className="text-sm font-medium text-gray-500">Physical Interfaces</label>
                                        <BridgeInterfaces control={control} bridgeIndex={bridgeIndex} interfaces={interfaces} validateUniqueness={validateInterfaceUniqueness} getValues={getValues} errors={errors} trigger={trigger} />
                                     </div>
                                ))}
                                <button type="button" onClick={() => append({ interfaces: [{ value: '' }], targetSubnet: '' })} className="btn-secondary text-sm flex items-center gap-2 mt-2"><AddIcon /> Add Vendo Bridge</button>
                            </div>
                        )}
                        {enablePppoe && (
                            <div className="pt-4 mt-4 border-t">
                                <h3 className="font-medium text-gray-800 px-2 mb-2">PPPoE Service Assignment</h3>
                                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                    <label htmlFor="pppoeInterfaceAssignment" className="font-medium text-sm text-gray-600">bridge-pppoe</label>
                                    <div className="md:col-span-2">
                                        <Controller name="pppoeInterfaceAssignment" control={control} rules={{ required: 'Required', validate: value => validateInterfaceUniqueness(value, 'pppoeInterfaceAssignment')}} render={({ field }) => (
                                            <select 
                                                {...field} 
                                                id="pppoeInterfaceAssignment" 
                                                className="input-class w-full" 
                                                onChange={e => {
                                                    field.onChange(e);
                                                    trigger("vendoInstances");
                                                }}
                                            >
                                                <option value="">-- Assign Physical Interface --</option>
                                                {interfaces.map(iface => <option key={iface} value={iface}>{iface}</option>)}
                                            </select>
                                        )} />
                                        {errors.pppoeInterfaceAssignment && <p className="text-red-500 text-xs mt-1">{errors.pppoeInterfaceAssignment.message}</p>}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </fieldset>
                
                <div className="text-right mt-6">
                    <button type="submit" className="btn-primary" disabled={isSubmitting || !isDirty}>
                        {isSubmitting ? 'Applying Updates...' : 'Apply Selected Updates'}
                    </button>
                </div>
            </form>
        </div>
    );
};